import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { analyticsClient } from "../../analytics/analyticsClient";
import { ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS } from "../../analytics/analyticsEvents";
import { getSiteSettingsReadSource, SiteSettingsReadSource } from "../../experiment/experiments";
import { T3pConnector } from "../../components/calendar/types";
import { isOptimisticallyConnected } from "../../utils/optimistic3pAuth";
import { logError, NamedError } from "../../utils/logger";
import { HTTPMethod, request, RequestErrorResponse, RequestErrorType } from "../fetchHelper";
import {
  AtlassianSite,
  CompanyThemingSettingsData,
  isUserAccount,
  Me,
  PermissionResponse,
  Resources,
  RovoEntitlementResponse,
  SettingsErrorType,
  SitesWithRovoEnabledResult,
  UserAccount,
} from "./types";

export enum GatewayQueryKeys {
  SEARCH_SITES_WITH_ROVO_ENABLED_WITH_METADATA_NEW = "searchSitesWithRovoEnabledWithMetadataNew",
}
export class GatewayError extends Error {
  errorType: RequestErrorType;
  statusCode: number;

  constructor(error: RequestErrorResponse) {
    super(error.errorMessage);
    this.errorType = error.errorType;
    this.statusCode = error.code;
  }
}

const fiveMinutesInMs: number = 5 * 60 * 1000; // five minutes, 60 seconds in a minutes, 1000 milliseconds in a second
const oneHourInMs: number = 60 * 60 * 1000; // sixty minutes in an hour, 60 seconds in a minutes, 1000 milliseconds in a second
const oneDayInMs: number = 24 * oneHourInMs;

export function useSitesWithRovoEnabled(
  userAccount?: UserAccount,
  useQueryOptions?: Partial<UseQueryOptions<SitesWithRovoEnabledResult, GatewayError>>,
): UseQueryResult<SitesWithRovoEnabledResult, GatewayError> {
  return useQuery<SitesWithRovoEnabledResult, GatewayError>({
    queryKey: [GatewayQueryKeys.SEARCH_SITES_WITH_ROVO_ENABLED_WITH_METADATA_NEW, userAccount?.accountId],
    ...useQueryOptions,
    queryFn: () => getSearchSitesWithRovoEnabled(),
    throwOnError(error) {
      logError({
        error: new NamedError("RovoEnabledSitesFetchError", error),
        impact: "Error fetching rovo enabled sites",
      });
      return false;
    },
    staleTime: fiveMinutesInMs,
    gcTime: oneHourInMs,
    retry: 1,
  });
}

export async function getSearchSitesWithRovoEnabled(): Promise<SitesWithRovoEnabledResult> {
  const sites = await getSearchSites();

  const siteMetadataList = sites.siteMetadata ?? [];
  const resourcesList = sites.resources ?? [];

  const rovoEnabledSitesMap: Record<string, { isRovoEnabled: boolean; orgId: string }> = {};

  for (let i = 0; i < siteMetadataList.length; i++) {
    const metadata = siteMetadataList[i];
    if (metadata.isRovoEnabled) {
      rovoEnabledSitesMap[metadata.siteId] = { isRovoEnabled: true, orgId: metadata.orgId };
    }
  }

  const rovoEnabledSites: Map<string, AtlassianSite> = new Map();

  for (const resource of resourcesList) {
    const siteId = resource.id.split("/").pop() ?? "";

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (rovoEnabledSitesMap[siteId]?.isRovoEnabled) {
      rovoEnabledSites.set(siteId, {
        cloudId: siteId,
        siteName: resource.siteName,
        baseUrl: resource.baseUrl,
        orgId: rovoEnabledSitesMap[siteId].orgId,
      });
    }
  }

  const metadata = {
    sitesFound: resourcesList.length,
    sitesWithRovoEnabled: rovoEnabledSites.size,
    rovoEnabledSitesMap,
  };

  if (!rovoEnabledSites.size) {
    throw new Error("No sites returned from configuration endpoint — possible transient failure");
  }

  return {
    results: Array.from(rovoEnabledSites.values()),
    metadata,
  };
}

async function getSearchSites(): Promise<Resources> {
  const url = "/gateway/api/xpsearch-aggregator/configuration/v1";
  const options = {
    method: "GET" as HTTPMethod.GET,
    credentials: "include" as RequestCredentials,
  };
  const response = await request<Resources>({ url, options });

  return response;
}

/**
 * Hook to fetch raw site resources from XPSearch Aggregator API.
 * This includes ARIs for all products available on each site.
 *
 * @returns Query result containing resources with ARIs and site metadata
 */
export function useSearchSiteResources(): UseQueryResult<Resources, GatewayError> {
  return useQuery<Resources, GatewayError>({
    queryKey: ["searchSiteResources"],
    queryFn: () => getSearchSites(),
    staleTime: fiveMinutesInMs,
    gcTime: oneHourInMs,
  });
}

export const useRovoEntitlement = (
  cloudId: string,
  options?: Omit<UseQueryOptions<RovoEntitlementResponse, Error>, "queryKey" | "queryFn">,
): UseQueryResult<RovoEntitlementResponse, Error> => {
  return useQuery({
    staleTime: 1000 * 60, // one minute
    ...options,
    queryFn: () => checkRovoEntitlement(cloudId),
    queryKey: ["rovoEntitlement", cloudId],
  });
};

export async function checkRovoEntitlement(cloudId: string): Promise<RovoEntitlementResponse> {
  const url = "/gateway/api/assist/rovo/v2/entitlement";
  const options = {
    method: "GET" as HTTPMethod.GET,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      "X-Cloudid": cloudId,
    }),
  };

  const response = await request<RovoEntitlementResponse>({
    url,
    options,
  });

  return response;
}

export function useUserAccount(): UseQueryResult<UserAccount, GatewayError> {
  return useQuery<UserAccount, GatewayError>({
    queryKey: ["userAccount"],
    queryFn: getUserAccount,
    retry: (retryAttempt: number, e: any) => {
      if (e.errorType === RequestErrorType.AUTH_ERROR) {
        return false;
      }

      return retryAttempt < 3;
    },
    refetchOnWindowFocus: false,
  });
}

async function getUserAccount(): Promise<UserAccount> {
  const url = "/gateway/api/me";
  const options = {
    method: "GET" as HTTPMethod.GET,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      Accept: "application/json",
    }),
  };
  const response = await request<Me>({ url, options });

  const userAccount = {
    accountId: response.account_id,
    email: response.email,
    name: response.name,
    nickname: response.nickname,
    picture: response.picture,
  };

  if (!isUserAccount(userAccount)) {
    logError({
      error: new Error("Invalid user account data"),
      extra: { userAccount },
    });
    throw new Error("Invalid user account data");
  }

  return userAccount;
}

export function extendSession(): void {
  const url = "/gateway/api/session/heartbeat";

  const options = {
    method: "POST" as HTTPMethod.POST,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({}),
  };

  request({ url, options }).catch(() => {});
}

async function get3PConnectors(cloudId: string): Promise<T3pConnector[]> {
  const url = `/gateway/api/third-party-configuration/connected-data-sources/${cloudId}`;
  const options = {
    method: "GET" as HTTPMethod.GET,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      "Content-Type": "application/json",
      "X-Cloudid": cloudId,
    }),
  };

  const response = await request<T3pConnector[]>({ url, options });
  return response;
}

function mergeOptimistic3pConnectors(data: T3pConnector[] | undefined): T3pConnector[] | undefined {
  if (!data) {
    return data;
  }
  // Do not clear the overlay when we see isUserOAuthed here: that data may be our own
  // optimistic update.
  return data.map((connector) =>
    isOptimisticallyConnected(connector.outboundAuthUrl) ? { ...connector, isUserOAuthed: true } : connector,
  );
}

export function use3PConnectors(cloudId: string): UseQueryResult<T3pConnector[] | undefined, GatewayError> {
  return useQuery<T3pConnector[], GatewayError, T3pConnector[] | undefined>({
    queryKey: ["3PConnectors", cloudId],
    queryFn: () => get3PConnectors(cloudId),
    enabled: Boolean(cloudId),
    select: mergeOptimistic3pConnectors,
  });
}

export async function getOrgAdminPermissions(
  orgId: string,
  cloudId: string,
  accountId: string,
): Promise<PermissionResponse> {
  const response = await request<PermissionResponse>({
    url: "gateway/api/permissions/permitted",
    options: {
      method: "POST" as HTTPMethod.POST,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "Content-Type": "application/json",
        "X-Cloudid": cloudId,
      }),
      body: JSON.stringify({
        resourceId: `ari:cloud:platform::org/${orgId}`,
        principalId: `ari:cloud:identity::user/${accountId}`,
        permissionId: "manage/org",
      }),
    },
  });

  return response;
}

export async function getSiteAdminPermissions(
  siteId: string,
  accountId: string,
  product: string,
): Promise<PermissionResponse> {
  const response = await request<PermissionResponse>({
    url: "gateway/api/permissions/permitted",
    options: {
      method: "POST" as HTTPMethod.POST,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "Content-Type": "application/json",
        "X-Cloudid": siteId,
      }),
      body: JSON.stringify({
        resourceId: `ari:cloud:${product}::site/${siteId}`,
        principalId: `ari:cloud:identity::user/${accountId}`,
        permissionId: "manage",
      }),
    },
  });

  return response;
}

export function useOrgAdminPermissions({
  orgId,
  cloudId,
  accountId,
}: {
  orgId: string | undefined;
  cloudId: string;
  accountId: string;
}): UseQueryResult<PermissionResponse, GatewayError> {
  return useQuery({
    queryKey: ["orgAdminAccess", orgId, accountId],
    queryFn: () => getOrgAdminPermissions(orgId as string, cloudId, accountId),
    staleTime: oneDayInMs,
    gcTime: oneDayInMs,
    enabled: Boolean(orgId),
  });
}

export function useSiteAdminPermissions({
  siteId,
  accountId,
  product = "platform",
}: {
  siteId: string | undefined;
  accountId: string;
  product?: string;
}): UseQueryResult<PermissionResponse, GatewayError> {
  return useQuery({
    queryKey: ["siteAdminAccess", siteId, accountId],
    queryFn: () => getSiteAdminPermissions(siteId as string, accountId, product),
    staleTime: fiveMinutesInMs,
    gcTime: fiveMinutesInMs,
    enabled: Boolean(siteId),
  });
}

const SETTINGS_ERROR_MESSAGES = {
  NETWORK_ERROR: "Network connection failed",
  VALIDATION_ERROR: (context: string): string => `Invalid ${context} data`,
  SERVER_ERROR: "Server error occurred",
  UNKNOWN_ERROR: "Failed to save changes",
};

const getSettingsErrorType = (error: unknown): SettingsErrorType => {
  if (error instanceof TypeError) {
    // Network/connection errors (fetch failures, DNS issues, etc.)
    return SettingsErrorType.NETWORK_ERROR;
  }
  if (error && typeof error === "object" && ("status" in error || "statusCode" in error)) {
    // HTTP error with status code (check both status and statusCode for compatibility)
    const status = (error as any).status ?? (error as any).statusCode;
    if (status >= 400 && status < 500) {
      return SettingsErrorType.VALIDATION_ERROR;
    }
    if (status >= 500) {
      return SettingsErrorType.SERVER_ERROR;
    }
    return SettingsErrorType.UNKNOWN_ERROR;
  }
  return SettingsErrorType.UNKNOWN_ERROR;
};

const handleSaveSettingsError = (error: unknown, context: string): never => {
  const errorType = getSettingsErrorType(error);
  const errorKey = SettingsErrorType[errorType] as keyof typeof SETTINGS_ERROR_MESSAGES;

  const messageTemplate = SETTINGS_ERROR_MESSAGES[errorKey];
  const message =
    errorType === SettingsErrorType.VALIDATION_ERROR
      ? (messageTemplate as Function)(context)
      : (messageTemplate as string);

  const settingsError = new GatewayError({
    errorMessage: message,
    code: errorType,
    errorType: RequestErrorType.OTHER_ERROR,
  });

  throw settingsError;
};

type SettingsReadOperation = "legacy_announcements_read" | "legacy_theming_read" | "rovoextadminsettings_read";

type SettingsReadResultAttributes = {
  siteSettingsReadSource: SiteSettingsReadSource;
  status: "success" | "failure";
  operation: SettingsReadOperation;
  failureReason?: "api_read_failed";
};

const trackSettingsReadResult = (attributes: SettingsReadResultAttributes): void => {
  analyticsClient
    .sendTrackEvent({
      action: "completed",
      actionSubject: ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS.SETTINGS_READ_RESULT,
      source: "extensionFrontend",
      attributes,
    })
    .catch((error) => {
      logError({ error, impact: "Failed to send settings read track event" });
    });
};

type SettingsMigrationResultAttributes = {
  siteSettingsReadSource: SiteSettingsReadSource;
  status: "success" | "failure";
  operation: "announcement_double_write" | "theming_double_write";
  failureReason?: "admin_settings_write_failed" | "legacy_write_failed";
};

const trackSettingsMigrationResult = (attributes: SettingsMigrationResultAttributes): void => {
  analyticsClient
    .sendTrackEvent({
      action: "completed",
      actionSubject: ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS.SETTINGS_MIGRATION_RESULT,
      source: "extensionFrontend",
      attributes,
    })
    .catch((error) => {
      logError({ error, impact: "Failed to send settings migration track event" });
    });
};

const getCompanyThemingAdminSettingsPayload = (data: CompanyThemingSettingsData): Partial<RovoExtAdminSettings> => ({
  companyThemingFontColorLight: data.fontColorLight ?? "",
  companyThemingFontColorDark: data.fontColorDark ?? "",
  companyThemingNavbarColorLight: data.navbarColorLight ?? "",
  companyThemingNavbarColorDark: data.navbarColorDark ?? "",
});

export type AnnouncementSettingsResponse = Array<{
  position: number;
  url: string;
  title: string;
  imageUrl: string;
}>;

export async function getAnnouncementSettings(siteId: string): Promise<AnnouncementSettingsResponse> {
  const settingKey = "rovo-extension-company-announcements";
  const encodedResourceAri = encodeURIComponent(`ari:cloud:rovo::site/${siteId}`);
  const url = `/gateway/api/rovo-extension-web/resource/${encodedResourceAri}/setting/${settingKey}`;

  const options = {
    method: HTTPMethod.GET,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      Accept: "application/json",
      "X-CloudId": siteId,
    }),
  };

  const siteSettingsReadSource = getSiteSettingsReadSource();

  try {
    const response = await request<{ data: { announcements?: AnnouncementSettingsResponse } }>({ url, options });
    trackSettingsReadResult({
      siteSettingsReadSource,
      status: "success",
      operation: "legacy_announcements_read",
    });
    return response.data.announcements ?? [];
  } catch (error) {
    trackSettingsReadResult({
      siteSettingsReadSource,
      status: "failure",
      operation: "legacy_announcements_read",
      failureReason: "api_read_failed",
    });
    throw error;
  }
}

export async function saveAnnouncementSettings(
  siteId: string,
  data: { announcements: Array<{ imageUrl: string; title: string; url: string; position: number }> },
): Promise<AnnouncementSettingsResponse> {
  const settingKey = "rovo-extension-company-announcements";
  const encodedResourceAri = encodeURIComponent(`ari:cloud:rovo::site/${siteId}`);
  const url = `/gateway/api/rovo-extension-web/resource/${encodedResourceAri}/setting/${settingKey}`;

  const options = {
    method: HTTPMethod.PUT,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CloudId": siteId,
    }),
    body: JSON.stringify(data),
  };

  const siteSettingsReadSource = getSiteSettingsReadSource();

  let announcements: AnnouncementSettingsResponse;
  try {
    const response = await request<{ data: { announcements?: AnnouncementSettingsResponse } }>({ url, options });
    announcements = response.data.announcements ?? [];
  } catch (error) {
    trackSettingsMigrationResult({
      siteSettingsReadSource,
      status: "failure",
      operation: "announcement_double_write",
      failureReason: "legacy_write_failed",
    });
    logError({
      error,
      impact: "Settings migration: failed to save announcement settings to legacy namespace during double-write",
      tags: {
        siteSettingsReadSource,
        settingsMigrationOperation: "announcement_double_write",
        settingsMigrationFailureReason: "legacy_write_failed",
      },
    });
    return handleSaveSettingsError(error, "announcement");
  }

  try {
    await saveRovoExtensionAdminSettingsRaw(siteId, { companyAnnouncements: data.announcements });
    trackSettingsMigrationResult({
      siteSettingsReadSource,
      status: "success",
      operation: "announcement_double_write",
    });
    return announcements;
  } catch (error) {
    trackSettingsMigrationResult({
      siteSettingsReadSource,
      status: "failure",
      operation: "announcement_double_write",
      failureReason: "admin_settings_write_failed",
    });
    logError({
      error,
      impact: "Settings migration: failed to save announcement settings to rovoextadminsettings during double-write",
      tags: {
        siteSettingsReadSource,
        settingsMigrationOperation: "announcement_double_write",
        settingsMigrationFailureReason: "admin_settings_write_failed",
      },
    });
    return handleSaveSettingsError(error, "announcement");
  }
}

export async function getCompanyThemingSettings(siteId: string): Promise<CompanyThemingSettingsData> {
  const settingKey = "rovo-extension-company-theming";
  const encodedResourceAri = encodeURIComponent(`ari:cloud:rovo::site/${siteId}`);
  const url = `/gateway/api/rovo-extension-web/resource/${encodedResourceAri}/setting/${settingKey}`;

  const options = {
    method: HTTPMethod.GET,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      Accept: "application/json",
      "X-CloudId": siteId,
    }),
  };

  const defaultCompanyThemingSettings: CompanyThemingSettingsData = {
    fontColorLight: null,
    fontColorDark: null,
    navbarColorLight: null,
    navbarColorDark: null,
  };

  const siteSettingsReadSource = getSiteSettingsReadSource();

  try {
    const response = await request<{ data: CompanyThemingSettingsData | undefined }>({ url, options });
    trackSettingsReadResult({
      siteSettingsReadSource,
      status: "success",
      operation: "legacy_theming_read",
    });
    return response.data ?? defaultCompanyThemingSettings;
  } catch (error) {
    trackSettingsReadResult({
      siteSettingsReadSource,
      status: "failure",
      operation: "legacy_theming_read",
      failureReason: "api_read_failed",
    });
    throw error;
  }
}

export async function saveCompanyThemingSettings(
  siteId: string,
  data: CompanyThemingSettingsData,
): Promise<CompanyThemingSettingsData> {
  const settingKey = "rovo-extension-company-theming";
  const encodedResourceAri = encodeURIComponent(`ari:cloud:rovo::site/${siteId}`);
  const url = `/gateway/api/rovo-extension-web/resource/${encodedResourceAri}/setting/${settingKey}`;

  const options = {
    method: HTTPMethod.PUT,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CloudId": siteId,
    }),
    body: JSON.stringify(data),
  };

  const siteSettingsReadSource = getSiteSettingsReadSource();

  let legacyThemingSettings: CompanyThemingSettingsData;
  try {
    const response = await request<{ data: CompanyThemingSettingsData }>({ url, options });
    legacyThemingSettings = response.data;
  } catch (error) {
    trackSettingsMigrationResult({
      siteSettingsReadSource,
      status: "failure",
      operation: "theming_double_write",
      failureReason: "legacy_write_failed",
    });
    logError({
      error,
      impact: "Settings migration: failed to save company theming settings to legacy namespace during double-write",
      tags: {
        siteSettingsReadSource,
        settingsMigrationOperation: "theming_double_write",
        settingsMigrationFailureReason: "legacy_write_failed",
      },
    });
    return handleSaveSettingsError(error, "company theme");
  }

  try {
    await saveRovoExtensionAdminSettingsRaw(siteId, getCompanyThemingAdminSettingsPayload(legacyThemingSettings));
    trackSettingsMigrationResult({
      siteSettingsReadSource,
      status: "success",
      operation: "theming_double_write",
    });
    return legacyThemingSettings;
  } catch (error) {
    trackSettingsMigrationResult({
      siteSettingsReadSource,
      status: "failure",
      operation: "theming_double_write",
      failureReason: "admin_settings_write_failed",
    });
    logError({
      error,
      impact: "Settings migration: failed to save company theming settings to rovoextadminsettings during double-write",
      tags: {
        siteSettingsReadSource,
        settingsMigrationOperation: "theming_double_write",
        settingsMigrationFailureReason: "admin_settings_write_failed",
      },
    });
    return handleSaveSettingsError(error, "company theme");
  }
}

export type MediaTokenResponse = {
  token: string;
};

export async function getMediaToken(siteId: string): Promise<MediaTokenResponse> {
  const response = await request<MediaTokenResponse>({
    url: "gateway/api/assist/rovo/v1/extension/media/token",
    options: {
      method: HTTPMethod.POST as HTTPMethod.POST,
      credentials: "include" as RequestCredentials,
      extraHeaders: new Headers({
        "X-Product": "rovo",
        "X-Experience-Id": "ai-mate",
        "X-Cloudid": siteId,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        collection: "rovo-extension",
      }),
    },
  });
  return response;
}

export interface RovoExtCompanyAnnouncement {
  title: string;
  imageUrl: string;
  url: string;
  position: number;
}

export interface RovoExtAdminSettings {
  controlledUserNtpEnabled?: boolean;
  controlledUserRovoCompanionEnabled?: boolean;
  controlledUserGdocsDefinitionsEnabled?: boolean;
  controlledUserDomContentEnabled?: boolean;
  controlledUserCompanyAnnouncements?: boolean;
  companyAnnouncements?: RovoExtCompanyAnnouncement[];
  customLogoCompanyQuery?: string;
  customLogoCompanyLink?: string;
  companyThemingFontColorLight?: string;
  companyThemingFontColorDark?: string;
  companyThemingNavbarColorLight?: string;
  companyThemingNavbarColorDark?: string;
  defaultNtpUrl?: string;
}

export async function getRovoExtensionAdminSettings(siteId: string): Promise<RovoExtAdminSettings> {
  const settingKey = "rovoextadminsettings";
  const encodedResourceAri = encodeURIComponent(`ari:cloud:rovo::site/${siteId}`);
  const url = `/gateway/api/rovo-extension-web/resource/${encodedResourceAri}/setting/${settingKey}`;

  const options = {
    method: HTTPMethod.GET,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      Accept: "application/json",
      "X-CloudId": siteId,
    }),
  };

  const siteSettingsReadSource = getSiteSettingsReadSource();

  try {
    const response = await request<{ data: RovoExtAdminSettings | undefined }>({ url, options });
    trackSettingsReadResult({
      siteSettingsReadSource,
      status: "success",
      operation: "rovoextadminsettings_read",
    });
    return response.data ?? {};
  } catch (error) {
    trackSettingsReadResult({
      siteSettingsReadSource,
      status: "failure",
      operation: "rovoextadminsettings_read",
      failureReason: "api_read_failed",
    });
    throw error;
  }
}

async function saveRovoExtensionAdminSettingsRaw(
  siteId: string,
  data: Partial<RovoExtAdminSettings>,
): Promise<RovoExtAdminSettings> {
  const settingKey = "rovoextadminsettings";
  const encodedResourceAri = encodeURIComponent(`ari:cloud:rovo::site/${siteId}`);
  const url = `/gateway/api/rovo-extension-web/resource/${encodedResourceAri}/setting/${settingKey}`;

  const options = {
    method: HTTPMethod.PUT,
    credentials: "include" as RequestCredentials,
    extraHeaders: new Headers({
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CloudId": siteId,
    }),
    body: JSON.stringify(data),
  };

  const response = await request<{ data: RovoExtAdminSettings }>({ url, options });
  return response.data;
}

export async function saveRovoExtensionAdminSettings(
  siteId: string,
  data: Partial<RovoExtAdminSettings>,
): Promise<RovoExtAdminSettings> {
  try {
    return await saveRovoExtensionAdminSettingsRaw(siteId, data);
  } catch (error) {
    return handleSaveSettingsError(error, "admin settings");
  }
}
